import { useCallback, useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import CountyTooltip from './components/CountyTooltip';
import MigrationMap from './components/MigrationMap';
import type { HoveredCounty, ViewName } from './components/MigrationMap';
import SidePanel from './components/SidePanel';
import { loadManifest } from './data/census';
import type { MigrationManifest } from './types';

export default function App() {
  const [manifest, setManifest] = useState<MigrationManifest | null>(null);
  const [year, setYear] = useState<number | null>(null);
  const [view, setView] = useState<ViewName>('Lower 48');
  const [hovered, setHovered] = useState<HoveredCounty | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mapReady, setMapReady] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    loadManifest(controller.signal)
      .then((data) => {
        setManifest(data);
        setYear(data.defaultYear ?? data.years.at(-1) ?? null);
      })
      .catch((err: Error) => {
        if (err.name !== 'AbortError') setError(err.message);
      });
    return () => controller.abort();
  }, []);

  const handleError = useCallback((message: string) => {
    setError((prev) => prev ?? message);
  }, []);

  const handleReady = useCallback(() => setMapReady(true), []);

  const stats = useMemo(
    () => (manifest && year ? manifest.stats[String(year)] : undefined),
    [manifest, year],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        width: '100%',
        overflow: 'hidden',
      }}
    >
      {manifest && year !== null && (
        <SidePanel
          manifest={manifest}
          year={year}
          onYearChange={setYear}
          view={view}
          onViewChange={setView}
          stats={stats}
        />
      )}

      {/* Map column */}
      <Box sx={{ position: 'relative', flexGrow: 1, minWidth: 0, height: '100%' }}>
        {year !== null && (
          <MigrationMap
            year={year}
            view={view}
            onHoverCounty={setHovered}
            onError={handleError}
            onReady={handleReady}
          />
        )}

        <CountyTooltip hovered={hovered} year={year ?? 0} />

        {!error && (!manifest || !mapReady) && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
              flexDirection: 'column',
              backgroundColor: 'rgba(255,255,255,0.7)',
              zIndex: 6,
            }}
          >
            <CircularProgress />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              Loading Census migration data…
            </Typography>
          </Box>
        )}

        {error && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 3,
              backgroundColor: 'rgba(255,255,255,0.92)',
              zIndex: 7,
            }}
          >
            <Alert severity="warning" sx={{ maxWidth: 560 }}>
              <AlertTitle>Can’t render the map yet</AlertTitle>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {error}
              </Typography>
              <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
                Checklist: <code>.env</code> contains <code>VITE_MAPBOX_TOKEN</code>, and{' '}
                <code>npm run data</code> has been run to populate <code>public/data</code>.
              </Typography>
            </Alert>
          </Box>
        )}
      </Box>
    </Box>
  );
}
